import { useEffect, useState } from 'react'

import { UPDATE_USER_KEY, updateUser } from '@/actions/userActions'
import { useAnalyticsTrack } from '@/features/amplitude'
import { getFetchError, getIsFetching } from '@/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Alert, Button, Checkbox, Dialog, Link, List, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { TosVersion } from '@/constants/businessConstants'

interface Props {
  currentUserId: number
}

export const TermsOfServiceModalV2 = ({ currentUserId }: Props) => {
  const dispatch = useAppDispatch()
  const [isAccepted, setIsAccepted] = useState(false)
  const track = useAnalyticsTrack()

  useEffect(() => {
    track('viewed terms of service update modal')
  }, [track])

  const isUpdating = useReselector(getIsFetching, UPDATE_USER_KEY)
  const error = useReselector(getFetchError, UPDATE_USER_KEY)

  const onClick = async () => {
    track('clicked terms of service update modal agree button')
    const data = {
      tosVersion: TosVersion.tos_01_2024,
      tosAcceptedOn: Date.now(),
    }
    /* Update user — don't need to do anything after because the returned user will close the modal */
    await updateUser(currentUserId, data)(dispatch)
  }

  return (
    <Dialog open>
      <Dialog.Content size="md">
        <Dialog.Title>
          Updates to Terms of Service and Privacy Policy
        </Dialog.Title>

        <div className="flex flex-col gap-2 bg-neutral-25 rounded-lg mx-auto p-4">
          <Dialog.Description className="flex-1 mb-2">
            <Text variant={TypographyStyles.BODY_MD}>
              We updated our Terms of Service and Privacy Policy, effective
              January 1, 2024. Please review and accept them to continue using
              Heard.
            </Text>
          </Dialog.Description>

          <div className="flex-1 mb-2">
            <Text variant={TypographyStyles.BODY_MD}>
              You can review them here:
            </Text>

            <List className="text-green-700 pt-2">
              <List.Item>
                <Link
                  newPage
                  variant="secondary"
                  href="https://www.joinheard.com/terms-of-service"
                  color={TextColors.BRAND}
                >
                  <Text
                    variant={TypographyStyles.BODY_MD}
                    color={TextColors.BRAND}
                  >
                    Terms of Service
                  </Text>
                </Link>
              </List.Item>

              <List.Item>
                <Link
                  newPage
                  variant="secondary"
                  href="https://www.joinheard.com/privacy"
                  color={TextColors.BRAND}
                >
                  <Text
                    variant={TypographyStyles.BODY_MD}
                    color={TextColors.BRAND}
                  >
                    Privacy Policy
                  </Text>
                </Link>
              </List.Item>
            </List>
          </div>
        </div>

        <div className="flex flex-col gap-2 mt-4">
          <div className="flex-1 mb-2">
            <Checkbox
              checked={isAccepted}
              className="text-md"
              onCheckedChange={(value) => setIsAccepted(value === true)}
              label={'I accept the updated Terms of Service and Privacy Policy'}
            />
          </div>
          {error && (
            <div className="flex-1 mb-2">
              <Alert
                variant="error"
                description="There was an error saving. Please try again."
              />
            </div>
          )}
        </div>
        <Dialog.Footer>
          <Button
            size="lg"
            variant="primary"
            onClick={onClick}
            disabled={!isAccepted || isUpdating}
            loading={isUpdating}
            className="ml-0"
          >
            I Accept
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
